import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { RequestDto } from '@78/shared';
import {
  MAX_FEE_BPS,
  deferFeeFor,
  graceOf,
  refundPercentOf,
  validateTerms,
  type RefundTier,
} from '@78/core';
import { createCounter, denyRequest, fetchCollections, fetchRequests } from '../lib/api.ts';
import { fetchOwnedNfts } from '../lib/chain.ts';
import { useActions } from '../lib/actions.ts';
import { getInitData } from '../lib/telegram.ts';
import { sameAddress, useCollectionNames, shortAddress } from '../lib/collections.ts';
import { useErrorText } from '../lib/errors.ts';
import { useFmt } from '../lib/format.tsx';

/** Цены человек называет целыми монетами, контракт держит нанотоны. */
const TON_UNIT = 1_000_000_000n;
const TIERS: RefundTier[] = [0, 1, 2, 3];

/**
 * Карточка запроса на доске — раскладка макета: превью модели, справа
 * премия к нашему флору, снизу три плитки с цифрами.
 *
 * Премия считается от нашего самого дешёвого оффера, а не от рынка: чужие
 * площадки мы не опрашиваем и выдавать одно за другое не будем.
 */
function RequestCard({
  request,
  floor,
  owned,
  onRespond,
}: {
  request: RequestDto;
  /** Самый дешёвый наш оффер в этой коллекции. null — офферов нет. */
  floor: string | null;
  /** Сколько таких гифтов у продавца на кошельке. */
  owned: number;
  onRespond: () => void;
}) {
  const { t } = useTranslation();
  const { money, duration } = useFmt();
  const nameOf = useCollectionNames();
  const collection = nameOf(request.collection);
  const premium =
    floor === null || BigInt(floor) === 0n
      ? null
      : Number(((BigInt(request.totalPrice) - BigInt(floor)) * 100n) / BigInt(floor));
  const now = Math.floor(Date.now() / 1000);
  const perPayment = (BigInt(request.totalPrice) - BigInt(request.upfront)) / BigInt(request.nPayments);

  return (
    <button
      className="card col"
      onClick={onRespond}
      style={{ textAlign: 'left', padding: 13, cursor: 'pointer', color: 'var(--ink)', font: 'inherit', gap: 11 }}
    >
      <div className="row" style={{ alignItems: 'flex-start', gap: 12, width: '100%' }}>
        <div
          className="thumb"
          style={{
            flexBasis: 44,
            height: 44,
            borderRadius: 11,
            padding: 0,
            backgroundImage: collection.image !== null ? `url(${collection.image})` : undefined,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Именной оффер называет вещь, оффер на коллекцию — коллекцию.
              До этого оба показывали коллекцию, и держатель не мог понять,
              просят у него лично или у всех сразу, — а разница решающая:
              на именной оффер, кроме него, ответить некому. */}
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {request.scope === 'item'
              ? request.itemName ?? shortAddress(request.nftAddress ?? '')
              : collection.name ?? shortAddress(request.collection)}
          </div>
          <div className="label" style={{ fontSize: 9.5, marginTop: 3, letterSpacing: 0 }}>
            {request.scope === 'item' && `${t('board.yours_personally')} · `}
            {floor !== null && `${t('board.floor_here', { floor: money(floor) })} · `}
            {owned > 0 && request.scope !== 'item'
              ? t('board.your_gifts_count', { n: owned })
              : `${t('board.request_expires')} ${duration(request.expiresAt - now)}`}
          </div>
        </div>
        <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
          <div className="mono num lime" style={{ fontSize: 17, fontWeight: 700 }}>
            {premium === null ? money(request.totalPrice) : `${premium > 0 ? '+' : ''}${premium}%`}
          </div>
          <div className="label label-xs">
            {premium === null ? t('board.total') : t('board.over_floor')}
          </div>
        </div>
      </div>

      <div className="stats3" style={{ width: '100%', gap: 7 }}>
        <div className="stat" style={{ padding: 8, borderRadius: 10 }}>
          <div className="label label-xs">{t('counter.down')}</div>
          <div className="stat-value" style={{ fontSize: 12.5 }}>{money(request.upfront)}</div>
        </div>
        <div className="stat" style={{ padding: 8, borderRadius: 10 }}>
          <div className="label label-xs">{t('board.payments')}</div>
          <div className="stat-value" style={{ fontSize: 12.5 }}>{request.nPayments}</div>
        </div>
        <div className="stat" style={{ padding: 8, borderRadius: 10 }}>
          <div className="label label-xs">{t('board.per_payment')}</div>
          <div className="stat-value" style={{ fontSize: 12.5 }}>{money(perPayment)}</div>
        </div>
      </div>
    </button>
  );
}

/**
 * Входящий запрос и ответ условиями — один экран, как в макете.
 *
 * Сверху то, что предлагают, крупным лаймовым блоком; ниже — что продавец
 * получит; потом его собственные условия. Порядок не случаен: сначала
 * выгода, потом обязательства.
 */
function CounterForm({
  request,
  onDone,
  onDeny,
}: {
  request: RequestDto;
  onDone: () => void;
  onDeny: () => void;
}) {
  const { t } = useTranslation();
  const { sum, money, duration } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();
  const nameOf = useCollectionNames();
  const qc = useQueryClient();

  const asked = BigInt(request.totalPrice);
  const [price, setPrice] = useState(Number(asked / TON_UNIT));
  /**
   * Какую вещь продавец отдаёт.
   *
   * На именном оффере выбора нет — просили вот эту. На оффере по коллекции
   * продавец выбирает из своих: раньше вещь в ответе не называлась вовсе, и
   * покупатель соглашался на условия, не зная предмета.
   */
  const [item, setItem] = useState<string | null>(request.nftAddress);
  // Свои вещи из этой коллекции — из чего выбирать. Для именного оффера не
  // спрашиваем: там вещь названа, и выбора нет.
  const mine = useQuery({
    queryKey: ['myNfts', actions.myAddress],
    queryFn: () => fetchOwnedNfts(actions.myAddress),
    enabled: actions.connected && request.scope === 'collection',
  });
  const fromCollection = (mine.data ?? []).filter(
    (g) => !g.onSale && g.collection !== null && sameAddress(g.collection, request.collection),
  );
  // Имя выбранной вещи. Уходит вместе с адресом: у покупателя списка
  // кошелька продавца нет, и без имени он увидит «EQA…x5Wq».
  const itemName =
    request.scope === 'item'
      ? request.itemName
      : fromCollection.find((g) => item !== null && sameAddress(g.address, item))?.name ?? null;
  const [downPct, setDownPct] = useState(
    Number((BigInt(request.upfront) * 100n) / (asked === 0n ? 1n : asked)),
  );
  const [payments, setPayments] = useState(request.nPayments);
  const [tier, setTier] = useState<RefundTier>(2);
  const [deferrals, setDeferrals] = useState(1);

  const total = BigInt(price) * TON_UNIT;
  const upfront = (total * BigInt(downPct)) / 100n;
  const installment = payments > 0 ? (total - upfront) / BigInt(payments) : 0n;
  const deferFee = deferFeeFor(total, upfront, payments);
  const now = Math.floor(Date.now() / 1000);
  const diff = total - asked;
  const collection = nameOf(request.collection);

  const problem = validateTerms(
    {
      totalPrice: total,
      upfront,
      deferFee,
      period: request.period,
      nPayments: payments,
      refundTier: tier,
      maxDeferrals: deferrals,
      feeBps: MAX_FEE_BPS,
      deadline: now + (payments + deferrals) * request.period + 86400,
    },
    now,
  );

  // Список кошелька приходит позже формы: пока его нет, выбирать не из
  // чего, а когда пришёл — первая вещь лучше пустоты.
  useEffect(() => {
    if (item === null && fromCollection[0] !== undefined) setItem(fromCollection[0].address);
  }, [item, fromCollection]);

  const send = useMutation({
    mutationFn: () =>
      createCounter(request.id, {
        initData: getInitData(),
        address: actions.myAddress,
        totalPrice: total.toString(),
        upfront: upfront.toString(),
        period: request.period,
        nPayments: payments,
        refundTier: tier,
        maxDeferrals: deferrals,
        deferFee: deferFee.toString(),
        nftAddress: item,
        itemName: itemName,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests'] });
      void qc.invalidateQueries({ queryKey: ['myCounters'] });
    },
  });

  if (send.isSuccess) {
    return (
      <div className="screen">
        <div className="card card-lime col">
          <strong className="title">{t('board.your_answer_sent')}</strong>
          <span className="text">{t('board.your_answer_hint')}</span>
        </div>
        <button className="btn" onClick={onDone}>{t('state.close')}</button>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="row card-flat" style={{ padding: '10px 13px', borderRadius: 13 }}>
        <span className="label">{t('board.request_expires')}</span>
        <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>
          {duration(request.expiresAt - now)}
        </span>
      </div>

      {/* Что именно уйдёт покупателю. На именном оффере это назвал он сам,
          на оффере по коллекции выбирает продавец — и выбирает здесь, а не
          потом в конструкторе: покупатель принимает условия вместе с
          вещью, а не условия отдельно и вещь сюрпризом. */}
      {request.scope === 'item' ? (
        <div className="card col" style={{ gap: 4 }}>
          <div className="label">{t('board.asked_this_item')}</div>
          <span className="text">{request.itemName ?? shortAddress(request.nftAddress ?? '')}</span>
        </div>
      ) : (
        fromCollection.length > 0 && (
          <div className="card col" style={{ gap: 6 }}>
            <div className="label">{t('board.which_item')}</div>
            <div className="chips">
              {fromCollection.map((g) => (
                <button
                  key={g.address}
                  aria-selected={item !== null && sameAddress(item, g.address)}
                  onClick={() => setItem(g.address)}
                >
                  {g.name ?? shortAddress(g.address)}
                </button>
              ))}
            </div>
          </div>
        )
      )}

      <div className="row" style={{ justifyContent: 'flex-start', gap: 12 }}>
        <div
          className="thumb"
          style={{
            flexBasis: 62,
            height: 62,
            borderRadius: 14,
            padding: 0,
            backgroundImage: collection.image !== null ? `url(${collection.image})` : undefined,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>
            {collection.name ?? shortAddress(request.collection)}
          </div>
          <div className="label" style={{ fontSize: 10, marginTop: 4 }}>{t('board.your_gift')}</div>
          {/* Премия словами — как в макете: проценты и сроки по отдельности
              продавец складывает в голове, а решение принимает по цифре,
              которую сложил кто-то другой. */}
          {diff !== 0n && (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {t('incoming.premium_words', {
                premium: `${diff > 0n ? '+' : '−'}${money(diff > 0n ? diff : -diff)}`,
                count: payments,
                days: Math.round((payments * request.period) / 86400),
              })}
            </div>
          )}
        </div>
      </div>

      {/* Сколько предлагают — единственное, ради чего продавец открыл экран. */}
      <div className="card" style={{ background: 'var(--lime)', color: 'var(--lime-ink)', padding: 16 }}>
        <div className="mono" style={{ fontSize: 8.5, letterSpacing: '0.16em', opacity: 0.75 }}>
          {t('incoming.offered_total')}
        </div>
        <div className="row" style={{ justifyContent: 'flex-start', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
          <span className="mono num" style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            {money(request.totalPrice)}
          </span>
        </div>
        <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 3 }}>
          {t('board.per_payment_note', {
            n: request.nPayments,
            days: duration(request.period * request.nPayments),
          })}
        </div>
      </div>

      {/* Свои условия. Уровень возврата назначает продавец: покупатель его не
          просил и увидит только в ответе. */}
      <div className="card col">
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}>
            {t('incoming.counter_terms')}
          </span>
          {diff !== 0n && (
            <span className={diff > 0n ? 'mono lime' : 'mono danger'} style={{ fontSize: 11 }}>
              {diff > 0n ? '+' : '−'}{money(diff > 0n ? diff : -diff)}
            </span>
          )}
        </div>

        <div className="row">
          <span className="text">{t('counter.total')}</span>
          <span className="mono num" style={{ fontWeight: 700 }}>{sum(total)}</span>
        </div>
        <input type="range" min={1} max={Math.max(Number(asked / TON_UNIT) * 3, 10)} value={price}
          onChange={(e) => setPrice(Number(e.target.value))} />

        <div className="row">
          <span className="text">{t('builder.down_payment')}</span>
          <span className="mono num" style={{ fontWeight: 700 }}>{downPct}% · {money(upfront)}</span>
        </div>
        <input type="range" min={10} max={50} value={downPct} onChange={(e) => setDownPct(Number(e.target.value))} />

        <div className="row">
          <span className="text">{t('counter.payments')}</span>
          <span className="mono num" style={{ fontWeight: 700 }}>{payments} × {money(installment)}</span>
        </div>
        <input type="range" min={1} max={52} value={payments} onChange={(e) => setPayments(Number(e.target.value))} />
      </div>

      <div className="card col">
        <div className="label label-lg">{t('builder.refund_level')}</div>
        <div className="stats2">
          {TIERS.map((x) => (
            <button
              key={x}
              className="stat"
              aria-selected={tier === x}
              onClick={() => setTier(x)}
              style={{
                textAlign: 'left',
                border: tier === x ? '1px solid var(--lime)' : '1px solid transparent',
                background: tier === x ? 'var(--lime-soft)' : 'var(--sf2)',
                color: 'var(--ink)',
                font: 'inherit',
                cursor: 'pointer',
                padding: 10,
              }}
            >
              <div className="mono" style={{ fontSize: 15, fontWeight: 700 }}>
                {String(refundPercentOf(x))}%
              </div>
              <div className="label label-xs" style={{ marginTop: 2 }}>{duration(graceOf(x))}</div>
            </button>
          ))}
        </div>

        <div className="row">
          <span className="text">{t('counter.deferrals')}</span>
          <span className="mono num">{deferrals} · {money(deferFee)}</span>
        </div>
        <input type="range" min={0} max={3} value={deferrals} onChange={(e) => setDeferrals(Number(e.target.value))} />
      </div>

      {problem !== null && <div className="card card-dng text">{t(`err.${problem}`)}</div>}
      {send.error !== null && send.error !== undefined && (
        <div className="card card-dng text">{errorText(send.error)}</div>
      )}

      <button
        className="btn"
        disabled={send.isPending || problem !== null || !actions.connected}
        onClick={() => send.mutate()}
      >
        {send.isPending ? '…' : t('board.answer_now')}
      </button>
      <button className="btn btn-ghost" onClick={onDeny}>{t('board.decline_request')}</button>
    </div>
  );
}

type Sort = 'premium' | 'expiry' | 'payments';

export function Board({ onBuilder }: { onBuilder: (nft?: string) => void }) {
  const { t } = useTranslation();
  const actions = useActions();
  const [responding, setResponding] = useState<RequestDto | null>(null);
  const [sort, setSort] = useState<Sort>('premium');
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['requests', actions.myAddress],
    queryFn: () => fetchRequests(undefined, actions.myAddress),
  });

  /**
   * Отказ от запроса.
   *
   * Раньше кнопка просто закрывала форму, и запрос оставался висеть у того
   * же человека при каждом открытии доски — понять, что отказ учтён, было
   * неоткуда. Теперь он убирается с его доски и больше не возвращается.
   *
   * Покупателю ничего не уходит: запрос адресован всем держателям
   * коллекции, и молчание одного — не событие.
   */
  const deny = useMutation({
    mutationFn: (id: number) =>
      denyRequest(id, { initData: getInitData(), address: actions.myAddress }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['requests'] }),
  });

  // Флор берём из каталога: в карточке нужна не сумма сама по себе, а
  // насколько она выше того, за что этот гифт уходит у нас сейчас.
  const { data: collections } = useQuery({ queryKey: ['collections'], queryFn: fetchCollections });

  // «Ваших N» — сколько таких гифтов у продавца на кошельке. Это и есть
  // ответ на вопрос «мне вообще есть что предложить».
  const { data: mine } = useQuery({
    queryKey: ['myNfts', actions.myAddress],
    queryFn: () => fetchOwnedNfts(actions.myAddress),
    enabled: actions.connected,
    staleTime: 5 * 60 * 1000,
  });

  if (responding !== null) {
    return (
      <CounterForm
        request={responding}
        onDone={() => setResponding(null)}
        onDeny={() => {
          deny.mutate(responding.id);
          setResponding(null);
        }}
      />
    );
  }

  const floorOf = (collection: string) =>
    collections?.collections.find((c) => c.collection === collection)?.floor ?? null;
  const ownedIn = (collection: string) =>
    (mine ?? []).filter((n) => n.collection === collection).length;

  // Свои запросы на доске не показываем: ответить на них всё равно нельзя,
  // а место они занимают.
  const requests = [...(data?.requests ?? [])]
    .filter((r) => r.buyer !== actions.myAddress)
    .sort((a, b) => {
      if (sort === 'expiry') return a.expiresAt - b.expiresAt;
      if (sort === 'payments') return a.nPayments - b.nPayments;
      // По премии: сравниваем отношение к флору, а не голые суммы — иначе
      // дорогая коллекция всегда наверху, и доска бесполезна.
      const rel = (r: RequestDto) => {
        const f = floorOf(r.collection);
        return f === null || BigInt(f) === 0n
          ? Number(BigInt(r.totalPrice) / 1_000_000n)
          : Number((BigInt(r.totalPrice) * 100n) / BigInt(f));
      };
      return rel(b) - rel(a);
    });

  return (
    <>
      <div>
        <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em' }}>
          {t('board.requests_gifts')}
        </div>
        <div className="text" style={{ marginTop: 4 }}>{t('board.buyers_have_already_named')}</div>
      </div>

      <div className="chips">
        {(['premium', 'expiry', 'payments'] as const).map((s) => (
          <button key={s} aria-selected={sort === s} onClick={() => setSort(s)}>
            {s === 'premium' ? t('board.sort_premium') : s === 'expiry' ? t('board.sort_expiry') : t('board.sort_payments')}
          </button>
        ))}
      </div>

      {isLoading && <div className="skeleton" style={{ minHeight: 130 }} />}

      {/* Пустое состояние зовёт выставить оффер: ждать спроса на доске —
          не единственный способ продать. */}
      {!isLoading && requests.length === 0 && (
        <div
          className="card col"
          style={{ borderStyle: 'dashed', background: 'transparent', gap: 9 }}
        >
          <div className="label">{t('board.empty_state')}</div>
          <strong className="title">{t('board.no_requests_other_gifts')}</strong>
          <span className="text">{t('board.don_t_wait_buyer')}</span>
          <button
            className="btn btn-sm"
            style={{ alignSelf: 'flex-start', width: 'auto' }}
            /* Со скобками, а не `onClick={onBuilder}`: React передал бы в
               обработчик событие клика, а первым аргументом здесь стоит
               адрес NFT — и конструктор падал на разборе «адреса», который
               на самом деле был объектом события. */
            onClick={() => onBuilder()}
          >
            {t('action.create_offer_instead')}
          </button>
        </div>
      )}

      {requests.map((r) => (
        <RequestCard
          key={r.id}
          request={r}
          floor={floorOf(r.collection)}
          owned={ownedIn(r.collection)}
          onRespond={() => setResponding(r)}
        />
      ))}
    </>
  );
}
